import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button, Text, Divider } from 'rsuite';
import dayjs from 'dayjs';
import pluralize from 'pluralize';
import { getTaxPaymentPeriodsAsync } from '../../store/slices/tax/taxThunks';
import AddAdditionalPeriodsModal from '../../components/tax/AddAdditionalPeriodsModal';
import { selectTaxState } from '../../store/slices/tax/taxSelectors';
import IconIndicator from '../../components/user-feedback/IconIndicator';
import Notice from '../../components/user-feedback/Notice';
import {
  clearPreSelectedTaxPeriod,
  resetTaxState,
  updateTaxFlowData,
} from '../../store/slices/tax/taxSlice';
import { C_TOT_PAYMENT_STATUS_INCOMPLETE } from '../../common/consts';
import { FLOWS_ENUM } from '../../common/enums';
import {
  selectConfigExpressRegistrationTypeVerbiage,
  selectConfigTaxPaymentPeriod,
  selectNumberOfProperties,
  selectBulkTax,
} from '../../store/slices/config/configSelectors';
import BasicStyledCheckbox from '../../components/form/BasicStyledCheckbox';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import { generateTaxablePeriods } from 'src/common/reportingPeriods';
import { useAppDispatch } from 'src/store/store';
import { PaymentPeriod, ReportingMethod } from '../../types/tax';
import { TaxLicensesTable } from '../../components/tax/TaxLicensesTable';
import SegmentedControlButtons from '../../components/common/SegmentedControlButtons';
import FlowLayout from 'src/components/layout/FlowLayout';

export const segmentedControlButtonOptions = [
  {
    value: ReportingMethod.INDIVIDUAL,
    label: 'Individually',
  },
  {
    value: ReportingMethod.BULK,
    label: 'Bulk Upload (Spreadsheet)',
  },
];

const TaxSelectProperties = () => {
  const dispatch = useAppDispatch();
  const placeNavigate = usePlaceNavigate();

  const [additionalPeriodsModalOpen, setAdditionalPeriodsModalOpen] =
    useState(false);
  const [availabelReportingPeriods, setAvailabelReportingPeriods] = useState<
    Array<PaymentPeriod>
  >([]);
  const [showNoSelectedLicenseError, setShowNoSelectedLicenseError] =
    useState(false);
  const [hideInactivePermits, setHideInactivePermits] = useState(true);

  const taxPaymentPeriod = useSelector(selectConfigTaxPaymentPeriod);

  const permitType = useSelector(selectConfigExpressRegistrationTypeVerbiage);
  const numberOfProperties = useSelector(selectNumberOfProperties);

  const isBulkTax = useSelector(selectBulkTax);
  const isBulkTaxCapable = useMemo(() => {
    if (!isBulkTax) {
      return false;
    }
    // Count the number of different licenses available
    const licenses = availabelReportingPeriods.flatMap(
      (period) => period.licenses
    );
    const licensesAvailable = [
      ...new Set(licenses.map((license) => license.license)),
    ];
    return licensesAvailable.length >= numberOfProperties;
  }, [numberOfProperties, isBulkTax, availabelReportingPeriods]);

  const {
    taxFlowData: formData,
    preSelectedPeriod,
    isTaxPaymentPeriodsSuccess,
    isGetTaxPaymentPeriodsInProgress,
    taxPaymentPeriodsErrorMsg,
  } = useSelector(selectTaxState);

  const generateSelectableTaxablePeriods = useCallback(
    (paymentPeriods: Array<PaymentPeriod>): void => {
      const periods = generateTaxablePeriods(
        paymentPeriods,
        formData.selectedLicenses,
        preSelectedPeriod,
        taxPaymentPeriod
      );

      setAvailabelReportingPeriods(periods);
    },
    [taxPaymentPeriod, formData.selectedLicenses, preSelectedPeriod]
  );
  useEffect(() => {
    dispatch(getTaxPaymentPeriodsAsync(generateSelectableTaxablePeriods));
  }, [generateSelectableTaxablePeriods, dispatch]);

  const selectedReportingPeriods = useMemo(
    () =>
      (availabelReportingPeriods?.filter((period) => period.selected) || [])
        .sort((a, b) =>
          dayjs(b?.startDate).isAfter(dayjs(a?.startDate)) ? 1 : -1
        )
        .map((period) => ({
          ...period,
          licenses:
            period.licenses?.filter((license) =>
              hideInactivePermits ? !license.isInactive : true
            ) || [],
        })),
    [availabelReportingPeriods, hideInactivePermits]
  );

  const [reportingMethod, setReportingMethod] = useState<ReportingMethod>();

  const toggleSelectableTaxablePeriods = (period: PaymentPeriod) => {
    const updatedReportingPeriods = [...availabelReportingPeriods];

    const existingPeriod = updatedReportingPeriods?.find(
      (item) =>
        item.startDate === period.startDate && item.endDate === period.endDate
    );

    if (existingPeriod) {
      if (existingPeriod.selected) {
        // currently selected, we need to deselct all licenses for the period
        deSelectAllLicensesInPeriod(existingPeriod);
      } else {
        // pre select licesnes for the period
        preSelectLicensesInPeriod(existingPeriod);
      }
      existingPeriod.selected = !existingPeriod.selected;
      setAvailabelReportingPeriods(updatedReportingPeriods);
      setShowNoSelectedLicenseError(false);
    }
  };

  const preSelectLicensesInPeriod = (period: PaymentPeriod) => {
    period.licenses = period.licenses.map((license) => ({
      ...license,
      selected: license.totPaidStatus === C_TOT_PAYMENT_STATUS_INCOMPLETE,
    }));
  };

  const deSelectAllLicensesInPeriod = (period: PaymentPeriod) => {
    period.licenses = period.licenses.map((license) => ({
      ...license,
      selected: false,
    }));
  };

  const toggleSelectedLicenseForPeriod = (
    licenseNum: string,
    period: PaymentPeriod
  ) => {
    const updatedReportingPeriods = [...availabelReportingPeriods];

    const existingPeriod = updatedReportingPeriods?.find(
      (item) =>
        item.startDate === period.startDate && item.endDate === period.endDate
    );

    if (existingPeriod) {
      const existingLicense = existingPeriod.licenses.find(
        (license) => license.license === licenseNum
      );

      if (existingLicense) {
        existingLicense.selected = !existingLicense.selected;
      }
      setAvailabelReportingPeriods(updatedReportingPeriods);
      setShowNoSelectedLicenseError(false);
    }
  };

  const toggleSelectAllLicensesForPeriod = (
    period: PaymentPeriod,
    isSelected: boolean
  ) => {
    const updatedReportingPeriods = [...availabelReportingPeriods];

    const existingPeriod = updatedReportingPeriods?.find(
      (item) =>
        item.startDate === period.startDate && item.endDate === period.endDate
    );

    if (existingPeriod) {
      existingPeriod.licenses = existingPeriod.licenses.map((license) => ({
        ...license,
        selected: isSelected,
      }));

      setAvailabelReportingPeriods(updatedReportingPeriods);
    }
  };

  const handleNextClick = (): Promise<boolean> => {
    // Check if at least one license has been selected for any year
    const selectedData = selectedReportingPeriods
      .filter(
        (period) =>
          period.selected && period.licenses.some((license) => license.selected)
      ) // Filter selected periods with at least one selected license
      .map((period) => ({
        ...period,
        licenses: period.licenses.filter((license) => license.selected), // Filter licenses to include only selected ones
      }));

    if (selectedData.length > 0) {
      if (!reportingMethod) {
        setShowNoSelectedLicenseError(true);
        return Promise.resolve(false);
      }

      // clear pre-selected payment periods in redux
      dispatch(clearPreSelectedTaxPeriod());
      // Save data in redux
      dispatch(
        updateTaxFlowData({
          selectedLicenses: selectedData,
          reportingMethod: reportingMethod,
        })
      );
      return Promise.resolve(true);
    }
    setShowNoSelectedLicenseError(true);
    return Promise.resolve(false);
  };

  const handleBackClick = (): Promise<boolean> => {
    dispatch(resetTaxState());
    placeNavigate('/dashboard');
    return Promise.resolve(true);
  };

  useEffect(() => {
    setReportingMethod(
      isBulkTaxCapable ? formData.reportingMethod : ReportingMethod.INDIVIDUAL
    );
  }, [formData.reportingMethod, isBulkTaxCapable]);

  return (
    <FlowLayout
      flow={FLOWS_ENUM.PAY_TOT}
      onBack={handleBackClick}
      onNext={handleNextClick}
      omitNavigationForward={!reportingMethod}
      showBack
      showNext
      title="Select Properties for Tax Reporting"
    >
      <Text className="mb-24">
        Your properties that are registered for tax reporting and remittance in
        the selected period are listed below. Please select the properties for
        which you wish to report at this time.
      </Text>
      {isGetTaxPaymentPeriodsInProgress && <IconIndicator />}
      {!isGetTaxPaymentPeriodsInProgress && (
        <>
          {isTaxPaymentPeriodsSuccess && (
            <>
              {selectedReportingPeriods.length &&
                selectedReportingPeriods?.map((period) => (
                  <div
                    className="mb-24"
                    key={`tax_period_${period.startDate}_${period.endDate}`}
                  >
                    <Text className="text-bold  header-text-h4 mb-16">
                      {period.label}
                    </Text>
                    <TaxLicensesTable
                      period={period}
                      permitType={permitType}
                      selectAll={toggleSelectAllLicensesForPeriod}
                      selectLicense={toggleSelectedLicenseForPeriod}
                    />
                  </div>
                ))}
              {!selectedReportingPeriods.length && (
                <Notice
                  message="No Payment periods Selected"
                  type="info"
                />
              )}
            </>
          )}
          {!isTaxPaymentPeriodsSuccess && (
            <Notice
              style={{ marginBottom: '15px' }}
              message={taxPaymentPeriodsErrorMsg}
            />
          )}
          <div className="responsive-stack">
            <Button
              className="btn-tertiary sm-100-percent"
              onClick={() => setAdditionalPeriodsModalOpen(true)}
            >
              Add Additional Periods
            </Button>

            <BasicStyledCheckbox
              onChange={(checked: boolean) => {
                setHideInactivePermits(checked);
              }}
              checked={hideInactivePermits}
              label={`Hide Inactive ${pluralize(permitType)}`}
            />
          </div>
          {isBulkTaxCapable && (
            <>
              <Divider />
              <Text className="text-large text-bold mb-12">
                How would you like to report tax for the selected properties?
              </Text>
              <Text className="mb-16">
                For tax reporting of {numberOfProperties} or more properties,
                you can report individually, or upload in bulk using a
                spreadsheet template on the next screen.
              </Text>
              <SegmentedControlButtons
                value={reportingMethod}
                setValue={setReportingMethod}
                options={segmentedControlButtonOptions}
              />
            </>
          )}
          {showNoSelectedLicenseError && (
            <Notice
              message={
                !reportingMethod
                  ? 'Please select a reporting method to continue'
                  : 'Please select one or more licenses to continue'
              }
              className="mt-12"
            />
          )}
        </>
      )}
      <AddAdditionalPeriodsModal
        availabelReportingPeriods={availabelReportingPeriods}
        updateAvailableReportingPeriods={toggleSelectableTaxablePeriods}
        isOpen={additionalPeriodsModalOpen}
        setOpen={setAdditionalPeriodsModalOpen}
        taxPaymentPeriod={taxPaymentPeriod}
      />
    </FlowLayout>
  );
};

export default TaxSelectProperties;
